// Arsenal and Strike bars (GDD §14): one tile per item with its hotkey, cost, doctrine and sponsor; clicking a tile
// buys it (GDD §7.1: the doctrine decides where it lands). An unaffordable tile is dimmed, and its fill shows how
// much of the price the treasury already covers, with the seconds until it does. A unit tile also shows whether its
// deploy site stands (GDD §7: a Port for ships, the capital or an Airbase for aircraft) as a green check or, when no
// city of yours can launch it, a red badge on a dashed red tile. Pointing at or focusing a tile reports it (onHover),
// so the Intel panel shows its details: the HUD uses no browser tooltips.

import type { RequirementCopy } from './catalog'
import { el, FractionSlot, TextSlot, button } from './dom'
import { HudError } from './errors'
import { formatMoney } from './format'
import type { BarItem } from './items'

const READY_LABEL = 'READY'
const NO_INCOME_LABEL = 'NO INCOME'

/** Whether one of your standing cities can launch a unit (a local hint: the sim decides). Keys RequirementCopy. */
export type SiteState = 'met' | 'unmet'

interface RequirementBadge {
  readonly copy: RequirementCopy
  readonly text: TextSlot
}

/** Reports the item under the pointer or keyboard focus; null when it leaves. */
export type HoverListener<T> = (subject: T | null) => void

/** Wires pointer enter/leave and focus/blur of `element` to `onHover`. */
export function reportHover<T>(element: HTMLElement, subject: T, onHover: HoverListener<T>, signal: AbortSignal): void {
  const enter = (): void => onHover(subject)
  const leave = (): void => onHover(null)
  element.addEventListener('pointerenter', enter, { signal })
  element.addEventListener('pointerleave', leave, { signal })
  element.addEventListener('focus', enter, { signal })
  element.addEventListener('blur', leave, { signal })
}

export class ItemTile<I extends BarItem> {
  readonly element: HTMLButtonElement
  private readonly fill: FractionSlot
  private readonly eta: TextSlot
  private readonly requirement: RequirementBadge | null

  constructor(
    readonly item: I,
    onBuy: (item: I) => void,
    onHover: HoverListener<I>,
    signal: AbortSignal,
  ) {
    const tile = button('tw-tile', '')
    tile.addEventListener('click', () => onBuy(item), { signal })
    reportHover(tile, item, onHover, signal)
    const head = el('span', 'tw-tile__head')
    head.append(el('span', 'tw-tile__key', item.hotkey.label), el('span', 'tw-tile__code', item.presentation.code))
    const eta = el('span', 'tw-tile__eta')
    tile.append(
      head,
      el('span', 'tw-tile__name', item.presentation.name),
      el('span', 'tw-tile__cost', formatMoney(item.cost)),
      el('span', 'tw-tile__doctrine', item.doctrine.tag),
    )
    const copy = item.requirement
    if (copy === null) {
      this.requirement = null
    } else {
      const badge = el('span', copy.needsBuilding ? 'tw-tile__req needs-building' : 'tw-tile__req', copy.met)
      tile.append(badge)
      this.requirement = { copy, text: new TextSlot(badge) }
    }
    tile.append(el('span', 'tw-tile__sponsor', item.presentation.sponsor), eta, el('span', 'tw-tile__fill'))
    this.element = tile
    this.fill = new FractionSlot(tile, '--tw-fill')
    this.eta = new TextSlot(eta)
  }

  update(money: number, revenuePerSec: number): void {
    const affordable = money >= this.item.cost
    this.element.classList.toggle('is-affordable', affordable)
    this.fill.set(money / this.item.cost)
    this.eta.set(affordable ? READY_LABEL : etaLabel(this.item.cost - money, revenuePerSec))
  }

  /** Shows whether one of your standing cities can launch this item right now (items with a requirement only). */
  setSiteState(state: SiteState): void {
    const requirement = this.requirement
    if (requirement === null) throw new HudError(`${this.item.presentation.name} has no deploy-site requirement`)
    requirement.text.set(requirement.copy[state])
    this.element.classList.toggle('is-blocked', state === 'unmet')
  }
}

function etaLabel(shortfall: number, revenuePerSec: number): string {
  if (revenuePerSec <= 0) return NO_INCOME_LABEL
  return `${Math.ceil(shortfall / revenuePerSec)}s`
}

export class PurchaseBar<I extends BarItem> {
  readonly element: HTMLElement
  readonly tiles: readonly ItemTile<I>[]

  constructor(title: string, items: readonly I[], onBuy: (item: I) => void, onHover: HoverListener<I>, signal: AbortSignal) {
    this.element = el('section', 'tw-bar tw-panel')
    this.element.setAttribute('aria-label', title)
    this.element.append(el('h2', 'tw-heading', title))
    const row = el('div', 'tw-bar__tiles')
    this.tiles = items.map((item) => new ItemTile(item, onBuy, onHover, signal))
    for (const tile of this.tiles) row.append(tile.element)
    this.element.append(row)
  }

  update(money: number, revenuePerSec: number): void {
    for (const tile of this.tiles) tile.update(money, revenuePerSec)
  }
}
